import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ProfilePageService } from './profile-page.service';
import { AuthenticationService } from '../../../../shared/services/authentication.service';
import { MatDialog } from '@angular/material/dialog';
import { EditModalComponent } from '../../../../modals/edit-modal/edit-modal.component';
import {
  CertificateUploadDialogComponent,
} from '../../../registration-pages/registration-page/forms/resume-form/certificate-upload-dialog/certificate-upload-dialog.component';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  ButtonSize,
  ButtonState,
  ButtonType,
  IBooking,
  IEducation,
  IMaterial,
  InputSize,
  InputState,
  IUser,
  IWorkExperience,
  MaterialType, UserRole,
} from '../../../../../assets/types/dtoTypes';
import { PATH } from '../../../../../assets/constants';
import languagesData from '../../../../../assets/language.json';
import { VideoService } from '../../../../shared/services/video.service';
import {
  BIPPERZ_BREAKPOINTS,
  BreakpointsService,
  MediaQueryResultMap,
} from '../../../../shared/services/breakpoint.service';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {
  WorkExperienceManagerComponent,
} from '../../../../modals/work-experience-manager/work-experience-manager.component';
import { ShowPopupService } from '../../../../shared/services/show-popup.service';
import { EducationManagerComponent } from '../../../../modals/education-manager/education-manager.component';
import { ImageService } from '../../../../shared/services/image.service';
import {DatePipe, DecimalPipe, NgClass, NgForOf, NgIf, NgStyle} from "@angular/common";
import {ExternalHeaderComponent} from "../../../../shared/external-header/external-header.component";
import {ButtonComponent} from "../../../../shared/button/button.component";
import {MatIcon} from "@angular/material/icon";
import {FooterComponent} from "../../../../shared/footer/footer.component";
import {LoadMaskComponent} from "../../../../shared/load-mask/load-mask.component";
import {AppInputAreaComponent} from "../../../../shared/text-areas/app-input-area.component";

@Component({
  selector: 'app-profile-page',
  templateUrl: './profile-page.component.html',
  styleUrls: ['./profile-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgClass,
    ExternalHeaderComponent,
    NgStyle,
    DecimalPipe,
    TranslateModule,
    MatIcon,
    ButtonComponent,
    DatePipe,
    FooterComponent,
    LoadMaskComponent,
    NgIf,
    NgForOf,
    AppInputAreaComponent,
    RouterLink
  ],
  standalone: true
})
export class ProfilePageComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private imageService = inject(ImageService);
  private popupModalService = inject(ShowPopupService);
  private videoService = inject(VideoService);
  private cdr = inject(ChangeDetectorRef);
  private breakpointsService = inject(BreakpointsService);
  private profilePageService = inject(ProfilePageService);
  private authService = inject(AuthenticationService);

  user: IUser | undefined;

  defaultProfileImage = 'assets/default-image.png';
  resolvedProfileImageUrl = this.defaultProfileImage;

  roleLoggedId: string = '';

  isMotivationTooShort = false;

  motivationMessage = '';

  bookings: IBooking[] = [];

  protected readonly InputSize = InputSize;

  protected readonly InputState = InputState;

  isMobile = false;

  breakpoints$: Observable<MediaQueryResultMap>;

  isLoading = false;

  isUserProfile = false;

  completionPercentage = 0;

  completionColor = 'red';

  loggedId: string;

  languageCountryMap: { [key: string]: string } = {};

  reviewsPath = PATH.REVIEWS;

  isEditingBio = false;

  editableBio: string = '';

  isBioExpanded = false;

  isImageLoaded = false;

  cv: IMaterial;

  certificates: IMaterial[];

  protected readonly ButtonSize = ButtonSize;

  protected readonly ButtonState = ButtonState;

  protected readonly ButtonType = ButtonType;

  joinMeetingTooltip: string = '';

  private readonly destroy$ = new Subject<void>();

  constructor();

  constructor() {
    languagesData.languages.forEach( ( lang ) => {
      this.languageCountryMap[lang.name] = lang.code;
    } );

    this.breakpointsService.observe( [ BIPPERZ_BREAKPOINTS.s, BIPPERZ_BREAKPOINTS.m_down ] ).subscribe( ( result ) => {
      this.isMobile = result.s || result.m_down;
    } );
  }

  ngOnInit() {
    this.translate.get( 'booking.join_meeting' ).subscribe( res => this.joinMeetingTooltip = res );
    this.route.params.subscribe( params => {
      this.loggedId = this.authService.getIdFromAccessToken();
      this.roleLoggedId = this.authService.getUserRoleFromAccessToken();
      const userId = params.id;
      this.isLoading = true;
      this.isUserProfile = this.loggedId === userId;
      this.reviewsPath = this.reviewsPath + '/' + userId;
      this.editableBio = this.user?.motivation || '';


      this.profilePageService.getProfileInfos( userId )
        .pipe( takeUntil( this.destroy$ ) )
        .subscribe( {
          next: ( result ) => {
            this.user = result;
            this.resolvedProfileImageUrl = this.defaultProfileImage;
            if ( this.user && this.user.materials.length > 0 ) {
              this.cv = this.user.materials?.find( material => material.type === MaterialType.CV ) ?? null;
              this.certificates = this.user.materials?.filter( material => material.type === MaterialType.OTHER );

              if ( this.user.materials && this.cv && !this.cv.url?.includes( '?sv=' ) ) {
                this.imageService.appendSasTokenToProfileImageUrl( this.cv.url, this.user.id ).toPromise().then( ( url ) => {
                  this.cv.url = url;
                  this.cdr.detectChanges();
                } );
              }

              if ( this.certificates?.length > 0 ) {
                const certPromises = this.certificates.map( cert =>
                  this.videoService.appendSasTokenToVideos( this.user.id, cert.url ).toPromise().then( fileUrlWithToken => ( {
                    ...cert,
                    fileUrl: fileUrlWithToken,
                  } ) ),
                );

                Promise.all( certPromises ).then( updatedCertificates => {
                  this.certificates = updatedCertificates;
                  this.cdr.detectChanges();
                } );
              }

            }

            if ( this.user.profileImageUrl && !this.user.profileImageUrl.includes( '?sv=' ) ) {
              this.imageService.appendSasTokenToProfileImageUrl( this.user.profileImageUrl, this.user.id ).toPromise().then( ( profileImageUrl ) => {
                this.user.profileImageUrl = profileImageUrl;
                this.resolvedProfileImageUrl = profileImageUrl || this.defaultProfileImage;
                this.cdr.detectChanges();
              } );
            } else {
              this.resolvedProfileImageUrl = this.user.profileImageUrl || this.defaultProfileImage;
            }


            this.isLoading = false;
            this.calculateProfileCompletion();
            this.cdr.detectChanges();
          },
          error: () => {
            this.isLoading = false;
          },
        } );
    } );
  }

  ngOnDestroy() {
    this.destroy$.complete();
  }

  getFlagSrc( language: string ): string {
    const code = this.languageCountryMap[language];
    if ( code ) {
      return `assets/icons/flags/square/${code}.svg`;
    } else {
      console.error( `No code found for language: ${language}` );
      return 'assets/icons/flags/square/default.svg';
    }
  }


  calculateProfileCompletion() {
    const motivationWords = this.user?.motivation?.trim().split( /\s+/ ).length ?? 0;
    this.isMotivationTooShort = motivationWords < 10;

    if ( this.isMotivationTooShort ) {
      this.translate.get( 'profile.motivation_too_short' ).subscribe( ( msg ) => {
        this.motivationMessage = msg;
      } );
    } else {
      this.motivationMessage = '';
    }

    const completedSections = [
      motivationWords >= 10,
      ( this.user?.languages?.length ?? 0 ) > 0,
      ( this.user?.education?.length ?? 0 ) > 0,
      ( this.user?.workExperience?.length ?? 0 ) > 0,
    ].filter( Boolean ).length;

    this.completionPercentage = ( completedSections / 4 ) * 100;
    this.completionColor = 'black';
  }

  deleteCertificate( certToDelete: IMaterial ): void {
    this.popupModalService
      .showConfirmationPopup( {
        status: 'warning',
        translationKey: 'job_details.popup.certificate.delete',
        isBackToLogin: false,
      } )
      .subscribe( ( confirmed: boolean ) => {
        if ( confirmed ) {
          this.certificates = this.certificates.filter( cert => cert.id !== certToDelete.id );

          this.profilePageService.deleteMaterial( certToDelete.id ).subscribe( () => {
            this.calculateProfileCompletion();
            this.cdr.detectChanges();
          } );
        }
      } );
  }


  onImageLoad() {
    this.isImageLoaded = true;
  }

  editProfileSection( section: string, data: { dataToEdit: boolean, section: string } ) {
    const sectionData = section === 'languages'
      ? { dataToEdit: section, languages: this.user?.languages || [] }
      : { dataToEdit: section, [section]: data };

    const dialogRef = this.dialog.open( EditModalComponent, {
      width: '50rem',
      disableClose: false,
      data: sectionData,
    } );

    dialogRef.afterClosed().subscribe( result => {
      if ( result ) {
        const updateMethod = `update${section.charAt( 0 ).toUpperCase() + section.slice( 1 )}`;

        if ( section === 'languages' ) {
          const newLanguages = result as string[];
          const oldLanguages = this.user?.languages || [];

          const hasChanges = JSON.stringify( oldLanguages.sort() ) !== JSON.stringify( newLanguages.sort() );

          if ( hasChanges ) {
            this.user!.languages = newLanguages;
            this.profilePageService.updateUser( this.user!.id, { languages: newLanguages } ).subscribe( () => {
              this.calculateProfileCompletion();
              this.cdr.markForCheck();
              this.cdr.detectChanges();
            } );
          }
        } else if ( this.profilePageService[updateMethod] ) {
          this.profilePageService[updateMethod]( this.user?.id, result ).subscribe( () => {
            this.user![section] = result;
            this.calculateProfileCompletion();
            this.cdr.markForCheck();
            this.cdr.detectChanges();
          } );
        }
      }
    } );
  }

  goToMainPage(): void {
    this.router.navigate( [ `${PATH.MAIN}/${this.loggedId}` ] );
  }

  toggleEditBio(): void {
    this.isEditingBio = true;
    this.editableBio = this.user?.motivation || '';
  }

  saveBio( $event: string ): void {
    if ( $event ) {
      this.profilePageService.updateMotivation( this.user?.id, $event )
        .pipe( takeUntil( this.destroy$ ) )
        .subscribe( () => {
          this.user.motivation = $event;
          this.isEditingBio = false;
          this.calculateProfileCompletion();
          this.cdr.detectChanges();
        } );
    }
  }

  toggleBioExpand(): void {
    this.isBioExpanded = !this.isBioExpanded;
  }

  openCertificateListDialog(): void {
    const dialogRef = this.dialog.open( CertificateUploadDialogComponent, {
      width: '600px',
      data: {
        userId: this.user.id,
      }
    } );

    dialogRef.afterClosed().subscribe( ( result: IMaterial ) => {
      if ( result?.title && result.url?.trim() ) {
        this.videoService.appendSasTokenToVideos( this.user.id, result.url ).subscribe( fileUrlWithToken => {
          const newCertificate: Partial<IMaterial> = {
            title: result.title,
            userId: this.user.id,
            url: fileUrlWithToken,
            type: MaterialType.OTHER,
            description: result.description || 'n/a',
          };

          this.profilePageService.createNewMaterial( newCertificate ).subscribe( ( newCertif ) => {
            ( this.certificates ??= [] ).push( newCertif );
            this.calculateProfileCompletion();
            this.cdr.detectChanges();
          } );
        } );
      }
    } );
  }


  editCv(): void {
    const dialogRef = this.dialog.open(CertificateUploadDialogComponent, {
      width: '600px',
      data: { userId: this.user.id },
    });

    dialogRef.afterClosed().subscribe((result: IMaterial) => {
      if (!result?.url) return;

      result.type = MaterialType.CV;
      result.userId = this.user.id;
      const oldCvId = this.cv?.id;

      // Immediately update the local cv
      this.videoService.appendSasTokenToVideos(this.user.id, result.url).subscribe((fileUrlWithToken) => {
        const updatedCv: IMaterial = {
          ...result,
          url: fileUrlWithToken,
        };

        if (oldCvId) {
          // Delete old CV first
          this.profilePageService.deleteMaterial(oldCvId).subscribe(() => {
            // Create the new CV
            this.profilePageService.createNewMaterial(updatedCv).subscribe((newCv) => {
              this.cv = newCv;

              // Replace it in user's materials
              if (this.user?.materials) {
                const idx = this.user.materials.findIndex(m => m.type === MaterialType.CV);
                if (idx !== -1) {
                  this.user.materials[idx] = newCv;
                } else {
                  this.user.materials.push(newCv);
                }
              }

              this.calculateProfileCompletion();
              this.cdr.markForCheck();
              this.cdr.detectChanges();
            });
          });
        } else {
          // No old CV, just create it
          this.profilePageService.createNewMaterial(updatedCv).subscribe((newCv) => {
            this.cv = newCv;
            if (!this.user.materials) {
              this.user.materials = [];
            }
            this.user.materials.push(newCv);

            this.calculateProfileCompletion();
            this.cdr.markForCheck();
            this.cdr.detectChanges();
          });
        }
      });
    });
  }


  deleteEducation( eduToDelete: IEducation ): void {
    this.popupModalService
      .showConfirmationPopup( {
        status: 'warning',
        translationKey: 'job_details.popup.education.delete',
        isBackToLogin: false,
      } )
      .subscribe( ( confirmed: boolean ) => {
        if ( confirmed ) {
          this.user!.education = this.user!.education.filter( edu =>
            !( edu.organization === eduToDelete.organization &&
              edu.education === eduToDelete.education &&
              edu.startDate === eduToDelete.startDate ),
          );

          this.profilePageService.updateUser( this.user!.id, { education: this.user!.education } )
            .subscribe( () => {
              this.calculateProfileCompletion();
              this.cdr.detectChanges();
            } );
        }
      } );
  }


  openEducationManager( educationToEdit?: IEducation ) {
    const dialogRef = this.dialog.open( EducationManagerComponent, {
      width: '600px',
      data: educationToEdit || {
        organization: '',
        education: '',
        educationLevel: '',
        location: '',
        startDate: '',
        completionDate: '',
        isCurrent: false,
      },
    } );

    dialogRef.afterClosed().subscribe( ( result: IEducation ) => {
      if ( result ) {
        if ( educationToEdit ) {
          const index = this.user!.education.findIndex(
            edu =>
              edu.organization === educationToEdit.organization &&
              edu.education === educationToEdit.education &&
              edu.startDate === educationToEdit.startDate,
          );

          if ( index !== -1 ) {
            this.user!.education[index] = result;
          }
        } else {
          this.user!.education = [ ...( this.user!.education ?? [] ), result ];
        }

        this.profilePageService.updateUser( this.user!.id, { education: this.user!.education } ).subscribe( () => {
          this.calculateProfileCompletion();
          this.cdr.detectChanges();
        } );
      }
    } );
  }

  deleteExperience( expToDelete: IWorkExperience ): void {
    this.popupModalService
      .showConfirmationPopup( {
        status: 'warning',
        translationKey: 'job_details.popup.experience.delete',
        isBackToLogin: false,
      } )
      .subscribe( ( confirmed: boolean ) => {
        if ( confirmed ) {
          this.user!.workExperience = this.user!.workExperience.filter( exp =>
            !( exp.jobTitle === expToDelete.jobTitle &&
              exp.organization === expToDelete.organization &&
              exp.startDate === expToDelete.startDate ),
          );

          this.profilePageService.updateUser( this.user!.id, { workExperience: this.user!.workExperience } )
            .subscribe( () => {
              this.calculateProfileCompletion();
              this.cdr.detectChanges();
            } );
        }
      } );
  }

  openWorkExperienceManager( experienceToEdit?: IWorkExperience ) {
    const dialogRef = this.dialog.open( WorkExperienceManagerComponent, {
      width: '600px',
      data: experienceToEdit || {
        jobTitle: '',
        organization: '',
        location: '',
        startDate: '',
        completionDate: '',
        isCurrent: false,
        jobDescription: '',
      },
    } );

    dialogRef.afterClosed().subscribe( ( result: IWorkExperience ) => {
      if ( result ) {
        if ( experienceToEdit ) {
          const index = this.user!.workExperience.findIndex(
            exp =>
              exp.jobTitle === experienceToEdit.jobTitle &&
              exp.organization === experienceToEdit.organization &&
              exp.startDate === experienceToEdit.startDate,
          );

          if ( index !== -1 ) {
            this.user!.workExperience[index] = result;
          }
        } else {
          this.user!.workExperience = [ ...( this.user!.workExperience ?? [] ), result ];
        }

        this.profilePageService.updateUser( this.user!.id, { workExperience: this.user!.workExperience } ).subscribe( () => {
          this.calculateProfileCompletion();
          this.cdr.detectChanges();
        } );
      }
    } );
  }

  navigateToApplicationTracking(): void {
    this.router.navigate( [ `${PATH.APPLICATIONS_OVERVIEW}/${this.user.id}` ] );
  }


  onCvClick(): void {
    if (!this.cv?.url || !this.user?.id) return;

    const cleanUrl = this.cv.url.split('?')[0]; // remove old token

    this.imageService.appendSasTokenToProfileImageUrl(cleanUrl, this.user.id)
      .toPromise()
      .then((signedUrl) => {
        const link = document.createElement('a');
        link.href = signedUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  }

  onCertificateClick(cert: IMaterial): void {
    if (!cert?.url || !this.user?.id) return;

    const cleanUrl = cert.url.split('?')[0];

    this.videoService.appendSasTokenToVideos(this.user.id, cleanUrl)
      .toPromise()
      .then((signedUrl) => {
        const link = document.createElement('a');
        link.href = signedUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    this.resolvedProfileImageUrl = this.defaultProfileImage;
    img.src = this.defaultProfileImage;
  }

  isCompany() {
    return this.roleLoggedId === UserRole.PARTNER;
  }
}
